import React, { useState } from 'react';
import {
  Badge, IconButton, Popover, List, ListItem, ListItemText, Typography, Box,
  Button, Divider, CircularProgress,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import gql from 'graphql-tag';
import { useRouter } from 'next/router';

const GET_UNREAD = gql`
  query myAdminMessagesUnreadCount { myAdminMessagesUnreadCount }
`;

const GET_RECEIVED = gql`
  query myAdminMessages($limit: Int, $offset: Int) {
    myAdminMessages(limit: $limit, offset: $offset) {
      id subject sentAt createdAt isRead
      sender { id surname lastname email }
    }
  }
`;

const MARK_ALL = gql`
  mutation markAllAdminMessagesRead { markAllAdminMessagesRead }
`;

function formatDate(v?: string) {
  if (!v) return '';
  const d = new Date(/^\d+$/.test(v) ? Number(v) : v);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  const h = Math.floor(diffMs / 3600000);
  const dd = Math.floor(diffMs / 86400000);
  if (min < 1) return "À l'instant";
  if (min < 60) return `Il y a ${min} min`;
  if (h < 24) return `Il y a ${h} h`;
  if (dd < 7) return `Il y a ${dd} j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

type Msg = {
  id: number | string;
  subject: string;
  sentAt?: string;
  createdAt?: string;
  isRead: boolean;
  sender?: { surname?: string; lastname?: string; email?: string } | null;
};

const MessagesBell = () => {
  const router = useRouter();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { data: countData, refetch: refetchCount } = useQuery(GET_UNREAD, {
    pollInterval: 60000,
    fetchPolicy: 'network-only',
  });
  const [fetchList, { data, loading }] = useLazyQuery(GET_RECEIVED, {
    variables: { limit: 20 },
    fetchPolicy: 'network-only',
  });
  const [markAll] = useMutation(MARK_ALL);

  const unread: number = (countData as any)?.myAdminMessagesUnreadCount || 0;
  const messages: Msg[] = ((data as any)?.myAdminMessages || []) as Msg[];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    fetchList();
  };
  const handleClose = () => setAnchorEl(null);

  const handleClick = (m: Msg) => {
    handleClose();
    router.push(`/notifications?adminMessage=${m.id}`);
  };

  const handleMarkAll = async () => {
    await markAll();
    refetchCount();
    fetchList();
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} aria-label="Messages" sx={{ mr: 1 }}>
        <Badge badgeContent={unread} color="error" max={99}>
          <MailOutlineIcon />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: { xs: '100vw', sm: 380 }, maxHeight: 480, mt: 1 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Messages OUAAA!</Typography>
          {unread > 0 && (
            <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAll} sx={{ textTransform: 'none' }}>
              Tout marquer lu
            </Button>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
            <MailOutlineIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">Aucun message reçu</Typography>
          </Box>
        ) : (
          <List sx={{ py: 0, overflowY: 'auto', maxHeight: 380 }}>
            {messages.map((m) => (
              <React.Fragment key={m.id}>
                <ListItem
                  onClick={() => handleClick(m)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: m.isRead ? 'transparent' : 'rgba(44,54,126,0.06)',
                    '&:hover': { bgcolor: 'action.hover' },
                    alignItems: 'flex-start',
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: m.isRead ? 400 : 600 }}>
                        {m.subject}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {m.sender ? `${m.sender.surname || ''} ${m.sender.lastname || ''}`.trim() : ''}
                        {m.sender ? ' · ' : ''}{formatDate(m.sentAt || m.createdAt)}
                      </Typography>
                    }
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
            <ListItem
              onClick={() => { handleClose(); router.push('/notifications'); }}
              sx={{ cursor: 'pointer', justifyContent: 'center', py: 1 }}
            >
              <Typography variant="caption" color="primary">Voir tous les messages</Typography>
            </ListItem>
          </List>
        )}
      </Popover>
    </>
  );
};

export default MessagesBell;
